import { NumericTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import type { InputHTMLAttributes } from "react";
import { memo } from "react";

import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { PAYMENT_CONSTRAINTS } from "../../utils/constants.ts";
import { LumpSumPaymentMobile } from "./mobile/lump-sum-payment-mobile/lump-sum-payment-mobile.tsx";

type LumpSumPaymentProps = {
	outstandingBalance: number;
	amount?: number;
	onAmountChange?: (amount: number) => void;
};

export const LumpSumPayment = memo<LumpSumPaymentProps>(
	({ outstandingBalance, amount = 0, onAmountChange }: LumpSumPaymentProps) => {
		const isMobile = useIsMobile();

		if (isMobile) {
			return (
				<LumpSumPaymentMobile
					amount={amount}
					onAmountChange={onAmountChange}
					outstandingBalance={outstandingBalance}
				/>
			);
		}

		return (
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: "1rem",
					marginBottom: "1rem",
				}}
			>
				<h5>Outstanding Balance</h5>
				<div style={{ display: "flex" }}>
					<h5 id={"lump-sum-payment-amount-label"}>Pay Lump Sum (USD)</h5>
					<span className={"required"}> *</span>
				</div>
				<h5>{formatCurrency(outstandingBalance)}</h5>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
						width: "25%",
					}}
				>
					$
					<NumericTextBox
						aria-labelledby={"lump-sum-payment-amount-label"}
						id={"lump-sum-payment-amount"}
						inputAttributes={
							{
								"data-testid": "pay-lump-sum-amount-input",
							} as InputHTMLAttributes<HTMLInputElement>
						}
						max={PAYMENT_CONSTRAINTS.MAX_AMOUNT}
						min={0}
						onChange={(e) => {
							// Always propagate (including clearing to 0) since value is controlled by amount
							const value = e.target.value;
							onAmountChange?.(value !== null && value < 0 ? 0 : (value ?? 0));
						}}
						placeholder={"5,000"}
						spinners={false}
						value={amount > 0 ? amount : null}
					/>
				</div>
			</div>
		);
	},
);

LumpSumPayment.displayName = "LumpSumPayment";
